import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from '@mui/material/Tooltip';

import SetupBubbles2 from '../../../../assets/SetupBubbles2.png';
import SetupLayers from '../../../../assets/SetupLayers.svg?react';
import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE, NORMS } from '../../../../constants/layers.ts';
import { usePedestrianLayerTypeEnabled } from '../../../../hooks/useIdlc1129Features';
import { greyP3 } from '../../../../theme.ts';
import { MeshGroup } from '../../../../types/mesh.ts';
import { layerTypeKeys, layerTypeLabel } from '../../../../utils/layerTypeLabel';
import { validatePhotometricTargets } from '../../../../utils/photometricTargets';
import { MeshGroupTree } from '../../../common/MeshGroupTree';
import { CustomTargetsForm } from '../../../photometric-targets/CustomTargetsForm';
import { LAYER_TYPES, LayerSetting } from '../../types';
import { LayerTypeIcon } from '../LayerTypeIcon';
import { LightingStandardsInfo } from './LightingStandardsInfo';

type SortKey = 'name' | 'type' | 'lmn';
type SortDir = 'asc' | 'desc';

interface NewLayerSidebarProps {
  isMobile: boolean;
  layerSettings: LayerSetting[];
  selectedLayer: string | null;
  hoveredLayer?: string | null;
  onHover: (name: string | null) => void;
  onSelect: (name: string) => void;
  onTypeChange: (name: string, type: keyof typeof LAYER_TYPES) => void;
  onToggleChange: (name: string, field: 'illuminated' | 'canHoldLight', value: boolean) => void;
  onNormChange: (name: string, value: string) => void;
  onCustomTargets: (name: string, targets: NonNullable<LayerSetting['customTargets']>) => void;
  onLayerHover: (name: string | null, color: string | null) => void;
  onValidationChange?: (isValid: boolean) => void;
  meshGroups?: MeshGroup[];
  onAddMeshGroup?: (parentGroupId?: string | null) => void;
  onRenameMeshGroup?: (uuid: string, name: string) => void;
  onDeleteMeshGroup?: (uuid: string) => void;
  onLayerGroupChange?: (layerUuid: string, groupId: string | null) => void;
}

export const NewLayerSidebar: React.FC<NewLayerSidebarProps> = ({
  layerSettings,
  selectedLayer,
  hoveredLayer,
  onHover,
  onSelect,
  onTypeChange,
  onToggleChange,
  onNormChange,
  onCustomTargets,
  onValidationChange,
  meshGroups = [],
  onAddMeshGroup,
  onRenameMeshGroup,
  onDeleteMeshGroup,
  onLayerGroupChange,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNormLayer, setExpandedNormLayer] = useState<string | null>(null);
  const [expandedTypeLayer, setExpandedTypeLayer] = useState<string | null>(null);
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const invalidLayers = layerSettings.filter(
    (layer) =>
      layer.illuminated &&
      !(layer.customTargets?.length && validatePhotometricTargets(layer.customTargets) === null) &&
      (!layer.norm || layer.norm === LAYER_NORM_NONE || layer.norm === LAYER_NORM_CUSTOM),
  );
  const isValid = invalidLayers.length === 0;

  // Notify parent of validation state changes
  React.useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  // Filter & sort layers
  const filteredLayers = useMemo(() => {
    const filtered = layerSettings.filter(
      (layer) =>
        layer.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        layer.originalName.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.displayName.localeCompare(b.displayName);
          break;
        case 'type':
          cmp = a.type.localeCompare(b.type);
          break;
        case 'lmn':
          cmp = (a.illuminated ? 1 : 0) - (b.illuminated ? 1 : 0);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [layerSettings, searchQuery, sortKey, sortDir]);

  const hasGroups = !!(onAddMeshGroup && onRenameMeshGroup && onDeleteMeshGroup && onLayerGroupChange);

  const renderLayerRow = (layer: LayerSetting) => (
    <React.Fragment key={layer.threeJsName}>
      <div
        draggable={hasGroups}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/layer-uuid', layer.uuid);
        }}
        onMouseEnter={() => onHover(layer.threeJsName)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onSelect(layer.threeJsName)}
        className={`
          h-[36px] pl-5 pr-6 flex items-center justify-between transition cursor-pointer
          ${selectedLayer === layer.threeJsName ? 'bg-red-400/15' : ''}
          ${hoveredLayer === layer.threeJsName && selectedLayer !== layer.threeJsName ? 'bg-orange-400/15' : ''}
        `}
        style={hoveredLayer !== layer.threeJsName && selectedLayer !== layer.threeJsName ? {} : undefined}
      >
        {/* Layer Name Info */}
        <div className="flex items-center gap-3 overflow-hidden mr-4">
          {/* Layer Type Button with Popup Dropdown */}
          <div className="relative">
            <button
              onClick={(e) => handleTypeClick(e, layer)}
              className={`w-6 h-6 flex items-center justify-center border rounded hover:border-gray-500 transition shrink-0 ${
                layer.type === 'Unspecified' ? 'bg-gray-600 border-gray-500' : 'bg-transparent border-black'
              }`}
              title={`Layer type: ${layer.type}`}
            >
              <LayerTypeIcon type={layer.type} style={{ width: 14, height: 14, color: 'white' }} />
            </button>
          </div>

          <span
            className="font-medium text-gray-100 truncate pl-1"
            style={{ fontSize: '14px' }}
            title={layer.displayName}
          >
            {layer.displayName}
          </span>
        </div>

        {/* Actions: Toggle & Norm */}
        <div className="flex items-center gap-6 shrink-0">
          {/* Illuminated Toggle - Track style */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleChange(layer.threeJsName, 'illuminated', !layer.illuminated);
            }}
            className="relative w-9 h-5 rounded-full bg-black transition-colors duration-200 ease-in-out focus:outline-none"
          >
            <div
              className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                layer.illuminated ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
              }`}
            />
          </button>

          {/* Norm Text/Button */}
          <div
            onClick={(e) => handleNormClick(e, layer)}
            className={`min-w-[54px] px-1 h-[28px] flex items-center justify-center ${
              layer.illuminated ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-50'
            }`}
            style={{ fontSize: '14px' }}
          >
            {layer.norm && layer.norm !== LAYER_NORM_NONE ? (
              <span style={{ color: layer.illuminated ? '#B3EC49' : greyP3 }}>
                {layer.norm === LAYER_NORM_CUSTOM ? t('workspace.customTargets.chip') : layer.norm}
              </span>
            ) : (
              <span className={layer.illuminated ? 'text-white' : `text-[${greyP3}]`}>{t('common.none')}</span>
            )}
          </div>
        </div>
      </div>

      {expandedNormLayer === layer.threeJsName && layer.illuminated && (
        <div className="px-6 py-6 bg-[#313131] border-t border-b border-gray-700 shadow-inner">
          {layer.customTargets?.length ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCustomTargets(layer.threeJsName, []);
              }}
              className="mb-3 text-[12px] text-gray-400 hover:text-white"
            >
              {t('workspace.customTargets.useClass')}
            </button>
          ) : (
            <>
              <div className="flex justify-between items-start mb-4">
                <p style={{ fontSize: '14px', color: greyP3 }}>{t('projects.newProject.chooseNorm')}</p>
              </div>

              <div className="grid grid-cols-6 gap-1.5 mb-3">
                {NORMS.map((norm) => (
                  <button
                    key={norm}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNormSelect(layer.threeJsName, norm);
                    }}
                    style={{ height: '28px', fontSize: '14px' }}
                    className={`px-1 rounded font-medium transition ${norm === LAYER_NORM_NONE ? 'col-span-2' : ''} ${
                      layer.norm === norm
                        ? `bg-lime-500 text-black`
                        : `bg-[#232323] text-white hover:bg-lime-500 hover:text-black`
                    }`}
                  >
                    {norm}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="mb-6">
            <CustomTargetsForm
              value={layer.customTargets ?? []}
              onApply={(targets) => onCustomTargets(layer.threeJsName, targets)}
            />
          </div>
          <div className="text-left">
            <LightingStandardsInfo />
          </div>
        </div>
      )}
    </React.Fragment>
  );

  const handleNormClick = (e: React.MouseEvent, layer: LayerSetting) => {
    e.stopPropagation();
    // Only allow norm selection when illuminated is checked
    if (!layer.illuminated) {
      return;
    }
    // Close type dropdown if open
    setExpandedTypeLayer(null);
    // Toggle the norm list expansion
    if (expandedNormLayer === layer.threeJsName) {
      setExpandedNormLayer(null);
    } else {
      setExpandedNormLayer(layer.threeJsName);
    }
  };

  const handleTypeClick = (e: React.MouseEvent, layer: LayerSetting) => {
    e.stopPropagation();
    // Close norm dropdown if open
    setExpandedNormLayer(null);
    // Toggle the type list expansion
    if (expandedTypeLayer === layer.threeJsName) {
      setExpandedTypeLayer(null);
    } else {
      // Get button position for dropdown placement
      const button = e.currentTarget as HTMLButtonElement;
      const rect = button.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
      setExpandedTypeLayer(layer.threeJsName);
    }
  };

  const handleTypeSelect = (layerName: string, type: keyof typeof LAYER_TYPES) => {
    onTypeChange(layerName, type);
    setExpandedTypeLayer(null);
  };

  // Handle norm selection - close the norm list after selecting
  const handleNormSelect = (layerName: string, norm: string) => {
    onNormChange(layerName, norm);
    // Close the norm selection view
    setExpandedNormLayer(null);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="h-[55px] flex items-center px-6 gap-3 relative overflow-hidden flex-shrink-0">
        <div className="p-2 rounded-[10px] bg-[#313131] flex-shrink-0">
          <SetupLayers className="w-4 h-4 text-lime-400" strokeWidth={2} />
        </div>

        <h1 className="text-xl md:text-2xl font-bold text-white truncate">{t('projects.newProject.layersTitle')}</h1>
        <img
          src={SetupBubbles2}
          alt="dots"
          className="absolute -top-[320px] -right-[200px] rotate-90 pointer-events-none select-none hidden md:block"
          style={{
            WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 60%)',
            maskImage: 'linear-gradient(to top, transparent 0%, black 60%)',
          }}
        />
      </div>

      {/* Search Bar */}
      <div className="px-6 py-3 flex-shrink-0">
        <div className="relative">
          <input
            type="text"
            placeholder={t('projects.newProject.searchLayers')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full px-4 py-2 pr-10 bg-black text-white text-sm rounded-lg outline-none focus:border-lime-400 focus:outline-none placeholder-gray-500 ${
              searchQuery ? 'border border-lime-400' : 'border border-transparent'
            }`}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              title={t('workspace.clearSearch')}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Filter Active Indicator */}
        <div className="mt-2 h-[15px] flex items-center gap-2 text-xs text-lime-400">
          {searchQuery && (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                {t('projects.newProject.showingLayers', {
                  filtered: filteredLayers.length,
                  total: layerSettings.length,
                })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Fixed Header — sortable columns */}
      <div className="pl-5 pr-6 py-2 flex items-center justify-between border-b border-[#313131]">
        <div className="flex items-center gap-4">
          <Tooltip title={t('workspace.typeTooltip')} arrow>
            <button
              onClick={() => toggleSort('type')}
              className={`text-xs flex items-center gap-0.5 w-6 transition ${sortKey === 'type' ? 'text-lime-400' : `text-[${greyP3}] hover:text-gray-300`}`}
            >
              {t('workspace.type')}
              {sortKey === 'type' && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
            </button>
          </Tooltip>
          <button
            onClick={() => toggleSort('name')}
            className={`text-xs flex items-center gap-0.5 transition ${sortKey === 'name' ? 'text-lime-400' : `text-[${greyP3}] hover:text-gray-300`}`}
          >
            {t('workspace.name')}
            {sortKey === 'name' && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
          </button>
        </div>
        <div className="flex justify-between items-start gap-9 shrink-20">
          <Tooltip title={t('workspace.lmnTooltip')} arrow>
            <button
              onClick={() => toggleSort('lmn')}
              className={`text-xs flex items-center gap-0.5 w-4 text-center transition ${sortKey === 'lmn' ? 'text-lime-400' : `text-[${greyP3}] hover:text-gray-300`}`}
            >
              {t('workspace.lmn')}
              {sortKey === 'lmn' && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
            </button>
          </Tooltip>
          <Tooltip title={t('workspace.normTooltip')} arrow>
            <span className={`text-xs text-[${greyP3}] w-[65px] text-center cursor-help`}>{t('workspace.norm')}</span>
          </Tooltip>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-[#313131] custom-scrollbar">
        {/* Mesh Group Tree */}
        {hasGroups ? (
          <MeshGroupTree
            meshGroups={meshGroups}
            onAddGroup={onAddMeshGroup}
            onRenameGroup={onRenameMeshGroup}
            onDeleteGroup={onDeleteMeshGroup}
            onDropLayerToGroup={onLayerGroupChange}
            getLayersForGroup={(groupId) =>
              filteredLayers.filter((l) => (groupId === null ? !l.groupId : l.groupId === groupId)).map((l) => l.uuid)
            }
            renderLayerItem={(layerUuid) => {
              const layer = filteredLayers.find((l) => l.uuid === layerUuid);
              if (!layer) return null;
              return renderLayerRow(layer);
            }}
          />
        ) : (
          filteredLayers.map((layer) => renderLayerRow(layer))
        )}

        {filteredLayers.length === 0 && (
          <div className="p-6 text-center text-gray-500" style={{ fontSize: '14px' }}>
            {searchQuery ? t('workspace.noLayersMatch') : t('projects.newProject.noLayersFound')}
          </div>
        )}
      </div>

      {/* Layer Type Popup Dropdown - rendered outside scroll container */}
      {expandedTypeLayer && (
        <div className="fixed inset-0 z-40" onClick={() => setExpandedTypeLayer(null)}>
          <div
            className="fixed bg-[#232323] rounded-md overflow-hidden min-w-[180px] z-50"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              boxShadow: '4px 6px 16px 0px rgba(0, 0, 0, 0.6)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dropdown Title */}
            <div className="px-3 py-1 bg-[#313131] max-h-[30px]">
              <span className="text-xs font-medium">{t('projects.newProject.setLayerType')}</span>
            </div>

            {/* Layer Type Options */}
            <div className="py-1">
              {layerTypeKeys(
                pedestrianLayerType,
                layerSettings.find((l) => l.threeJsName === expandedTypeLayer)?.type,
              ).map((type) => {
                const currentLayer = layerSettings.find((l) => l.threeJsName === expandedTypeLayer);
                return (
                  <button
                    key={type}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect(expandedTypeLayer, type as keyof typeof LAYER_TYPES);
                    }}
                    className={`layer-icon-container w-full flex items-center gap-2 px-3 py-2 text-left transition ${
                      currentLayer?.type === type
                        ? 'bg-green02 text-black'
                        : 'text-white hover:bg-green01 hover:text-black'
                    }`}
                  >
                    <LayerTypeIcon type={type as keyof typeof LAYER_TYPES} style={{ width: 14, height: 14 }} />
                    <span className="text-sm">{layerTypeLabel(t, type)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
